/**
 * Utility service for generating and downloading CSV files
 */

export interface CSVDownloadOptions {
  data: (string | number)[][];
  filename: string;
  headers?: string[];
}

/**
 * Downloads data as a CSV file
 * @param options - Configuration options for the CSV download
 */
export const downloadCSV = ({ data, filename, headers }: CSVDownloadOptions): void => {
  // Prepare rows starting with headers if provided
  const rows = headers ? [headers, ...data] : data;
  
  // Convert to CSV format - escape quotes and wrap each cell in quotes
  const csvContent = rows
    .map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`)
        .join(',')
    )
    .join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  URL.revokeObjectURL(url);
};

/**
 * Helper function specifically for glucose data export
 * @param samples - Product glucose samples
 * @param baselineSamples - Baseline glucose samples (optional)
 * @param filename - Custom filename (optional, defaults to timestamped name)
 */
export const downloadGlucoseDataCSV = (
  samples: number[][],
  baselineSamples?: number[][],
  filename?: string
): void => {
  const headers = [
    'Product meal offset (s)',
    'Product glucose level (mg/dl)',
    'Baseline meal offset (s)',
    'Baseline glucose level (mg/dl)'
  ];
  
  // Get max length to ensure all data is included
  const maxLength = Math.max(samples.length, baselineSamples?.length || 0);
  
  const data: (string | number)[][] = [];
  
  for (let i = 0; i < maxLength; i++) {
    const productOffset = samples[i] ? samples[i][0] : '';
    const productGlucose = samples[i] ? samples[i][1] : '';
    const baselineOffset = baselineSamples && baselineSamples[i] ? baselineSamples[i][0] : '';
    const baselineGlucose = baselineSamples && baselineSamples[i] ? baselineSamples[i][1] : '';
    
    data.push([productOffset, productGlucose, baselineOffset, baselineGlucose]);
  }
  
  const finalFilename = filename || `glucose_data_${Date.now()}.csv`;
  
  downloadCSV({
    data,
    headers,
    filename: finalFilename
  });
};